<script setup>
import { useStore } from 'vuex'
import SoilProfileSection from './SoilProfileSection.vue'
import { debounce } from 'lodash'
import { computed, ref, watch } from 'vue'

const store = useStore()

const baseSoilProfile = ref({
  id: null,
  soil_composition: null,
  x: null,
  x_start: null,
  x_end: null
})

const selectedStartRelativeValue = ref(1 / 2)
const selectedEndRelativeValue = ref(1 / 2)
const selectedStartMethod = ref('distance')
const selectedEndMethod = ref('distance')
const index = ref(null)
const startRule = ref([])
const endRule = ref([])

const dialog = computed(() => store.state.soilProfile.dialog)
const loading = computed(() => store.state.soilProfile.loading)
const soilProfileSections = computed(() => store.state.soilProfile.soilProfileSections)

const soilProfilePointItems = computed(() => store.state.soilProfilePoint.items)

const soilCompositionId = computed(
  () => store.getters['stabSchematisationStage/currentCompositionId']
)

const soilProfileBlock = computed(() => store.getters['soilProfile/getSortedSoilProfile'])

const previousSoilProfileBlock = computed(() => {
  if (!soilProfileBlock.value.length || index.value === null || index.value === 0) return null
  return index.value === -1
    ? soilProfileBlock.value[soilProfileBlock.value.length - 1]
    : soilProfileBlock.value[index.value - 1]
})

const nextSoilProfileBlock = computed(() => {
  if (!soilProfileBlock.value.length || index.value === -1) return null
  return soilProfileBlock.value[index.value]
})
const checkExistSoilProfile = computed(() => {
  return soilProfileBlock.value.some((item) => {
    return item.x === baseSoilProfile.value.x
  })
})

const xRule = computed(() => {
  const errorText = 'There already is a SoilProfile at the selected location.'
  return checkExistSoilProfile.value ? [errorText] : []
})

const disableSaved = computed(() => {
  return (
    checkExistSoilProfile.value ||
    baseSoilProfile.value.x === null ||
    !!startRule.value.length ||
    !!endRule.value.length
  )
})
watch(selectedStartRelativeValue, () => {
  updateParameter()
})
watch(selectedEndRelativeValue, () => {
  updateParameter()
})

watch(
  () => baseSoilProfile.value.x_start,
  () => {
    getStartRule()
  }
)

watch(
  () => baseSoilProfile.value.x_end,
  () => {
    getEndRule()
  }
)
const createSoilProfile = (item) => store.dispatch('soilProfile/createSoilProfile', item)
const toggleDialog = () => store.dispatch('soilProfile/toggleDialog')
const calculateSection = (payload) => store.dispatch('soilProfile/calculateSection', payload)
const fetchSoilProfileItems = (id) => store.dispatch('soilProfile/fetchItems', id)
const resetSoilProfileSections = () => store.dispatch('soilProfile/resetSoilProfileSections')

const generateSoilLayer = (id) => store.dispatch('soilLayer/generate', id)

const fetchStatePoints = (id) => store.dispatch('statePoints/fetchItems', id)
const close = () => {
  toggleDialog()
}
const disabledHeightMethod = (section) => {
  return !soilProfileSections.value[section + '_height_available']
}
const resetBase = () => {
  baseSoilProfile.value.x = null
  baseSoilProfile.value.x_start = null
  baseSoilProfile.value.x_end = null
  index.value = null
}
const updateInput = async (text) => {
  const x = text === '' ? null : parseFloat(text)
  await updateInputWithDebounce(x)
}
const updateInputWithDebounce = debounce(async function (x) {
  await setXParameter(x)
  getStartRule()
  getEndRule()
}, 300)

const getStartRule = () => {
  let rules = []
  if (
    previousSoilProfileBlock.value &&
    (baseSoilProfile.value.x_start === null ||
      baseSoilProfile.value.x_start < previousSoilProfileBlock.value.x)
  ) {
    rules.push(
      'Start section value should be higher than X value of the previous soil profile block'
    )
  }
  if (
    baseSoilProfile.value.x_start !== null &&
    baseSoilProfile.value.x_start > baseSoilProfile.value.x
  ) {
    rules.push('Start section value should not be higher than X value')
  }
  startRule.value = rules
}
const getEndRule = () => {
  let rules = []
  if (
    nextSoilProfileBlock.value &&
    (baseSoilProfile.value.x_end === null ||
      baseSoilProfile.value.x_end > nextSoilProfileBlock.value.x)
  ) {
    rules.push('End section value should be lower than X value of the next soil profile block')
  }
  if (
    baseSoilProfile.value.x_end !== null &&
    baseSoilProfile.value.x_end < baseSoilProfile.value.x
  ) {
    rules.push('End section value should not be lower than X value')
  }
  endRule.value = rules
}
const setXParameter = async (x) => {
  if (x !== null) {
    baseSoilProfile.value.x = x
    index.value = soilProfileBlock.value.findIndex((item) => {
      return item.x > baseSoilProfile.value.x
    })
    if (!checkExistSoilProfile.value) {
      await updateParameter()
    }
  } else {
    resetBase()
    await resetSoilProfileSections()
  }
}
const updateParameter = async () => {
  const isValid = baseSoilProfile.value.x !== null && !isNaN(baseSoilProfile.value.x)
  if (isValid) {
    await calculateSection({
      x_location_of_new_profile: baseSoilProfile.value.x,
      method_start: selectedStartMethod.value,
      method_end: selectedEndMethod.value,
      relative_value_start: selectedStartRelativeValue.value,
      relative_value_end: selectedEndRelativeValue.value
    })
    baseSoilProfile.value.x_start = soilProfileSections.value.x_start
    baseSoilProfile.value.x_end = soilProfileSections.value.x_end
  }
}
const setSectionParameter = (input, section) => {
  baseSoilProfile.value[section] = input === '' && !isNaN(input) ? null : parseFloat(input)
}
const save = async () => {
  await createSoilProfile(baseSoilProfile.value)
  resetBase()
  await resetSoilProfileSections()
  close()
  await fetchSoilProfileItems(soilCompositionId.value)
  if (soilProfilePointItems.value.length) {
    await getSoilLayer()
  }
}
const getSoilLayer = async () => {
  try {
    await generateSoilLayer(soilCompositionId.value)
    await fetchStatePoints(soilCompositionId.value)
  } catch (e) {
    console.log(e)
  }
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="400" @click:outside="close">
    <v-card variant="flat" ref="soil_profile">
      <v-card-title class="pt-4">Create soil profile block</v-card-title>
      <v-card-text class="pl-4 pt-0">
        <v-row no-gutters>
          <v-col cols="12" class="pb-0">
            <v-text-field
              color="primary"
              label="X meters relative to outer crest"
              class="text-field"
              :rules="xRule"
              type="number"
              @update:model-value="updateInput"
              @keydown.enter="updateInput(baseSoilProfile.x)"
              :model-value="baseSoilProfile.x"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12">
            <SoilProfileSection
              section="x_start"
              :model-value="baseSoilProfile"
              :selected-method="selectedStartMethod"
              :disabled-height-method="disabledHeightMethod('x_start')"
              :rule="startRule"
              @updateValue="setSectionParameter($event, 'x_start')"
              @updateMethod="(selectedStartMethod = $event), updateParameter()"
              @setMethod="selectedStartMethod = $event"
              @updateRelative="selectedStartRelativeValue = $event"
            />
          </v-col>
          <v-col cols="12" class="mt-4">
            <SoilProfileSection
              section="x_end"
              :model-value="baseSoilProfile"
              :selected-method="selectedEndMethod"
              :disabled-height-method="disabledHeightMethod('x_end')"
              :rule="endRule"
              @updateValue="setSectionParameter($event, 'x_end')"
              @updateMethod="(selectedEndMethod = $event), updateParameter()"
              @setMethod="selectedEndMethod = $event"
              @updateRelative="selectedEndRelativeValue = $event"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn :loading="loading" color="primary" text @click="save" :disabled="disableSaved">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
